import {
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import axios from "axios";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { isValidPassword } from "../../utils/Password";

const OnBoarding_PersonalDetails = forwardRef((props, ref) => {
  const { personalDetails, setPersonalDetails, setDisableContinue } = props;

  const [alertMessage, setAlertMessage] = useState("");
  const [idExists, setIdExists] = useState(false);
  const [loading, setLoading] = useState(false);

  // Expose the validate function to parent components
  useImperativeHandle(ref, () => ({
    validate,
  }));

  useEffect(() => {
    // Ensure the "Continue" button is disabled on the initial load
    setDisableContinue(true);
  }, [setDisableContinue]);

  const validate = async () => {
    setAlertMessage(""); // Clear previous messages

    if (!personalDetails.idNumber || personalDetails.idNumber.length !== 13 || validateLuhn(personalDetails.idNumber) === false) {
      setAlertMessage("The ID Number is invalid.");
      return false;
    }
    if (!personalDetails.firstName || !personalDetails.lastName) {
      setAlertMessage("Please enter your first and last name.");
      return false;
    }
    if (!/^\d+$/.test(personalDetails.idNumber)) {
      setAlertMessage("ID number must be numeric.");
      return false;
    }
    if (!validateLuhn(personalDetails.idNumber)) {
      setAlertMessage("ID number is invalid.");
      return false;
    }
    if (!personalDetails.mobile || personalDetails.mobile.length !== 10) {
      setAlertMessage("Mobile number must be 10 digits long.");
      return false;
    }
    if (!/^\d+$/.test(personalDetails.mobile)) {
      setAlertMessage("Mobile number must contain only digits.");
      return false;
    }
    if (!personalDetails.email.includes("@")) {
      setAlertMessage("Invalid email address.");
      return false;
    }
    if (personalDetails.password.length < 8) {
      setAlertMessage("Password must be at least 8 characters long.");
      return false;
    }
    if (!isValidPassword(personalDetails.password)) {
      setAlertMessage("Password must include at least one special character.");
      return false;
    }
    if (personalDetails.password !== personalDetails.confirmPassword) {
      setAlertMessage("Passwords do not match.");
      return false;
    }

    const idValid = await checkIdNumber();
    if (!idValid) {
      return false;
    }

    // All validations passed
    setDisableContinue(false);
    return true;
  };

  const validateLuhn = (idNumber) => {
    let sum = 0;
    let alternate = false;
    for (let i = idNumber.length - 1; i >= 0; i--) {
      let n = parseInt(idNumber[i], 10);
      if (alternate) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      alternate = !alternate;
    }
    return sum % 10 === 0;
  };

  const checkIdNumber = async () => {
    if (!personalDetails.idNumber || personalDetails.idNumber.length !== 13) {
      setAlertMessage("ID number must be 13 characters long.");
      return false;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/check-id-number`,
        { idNumber: personalDetails.idNumber },
        {
          headers: {
            "x-api-key": process.env.REACT_APP_BACKEND_API_KEY,
          },
        }
      );

      if (response.data.error) {
        setIdExists(true);
        setAlertMessage(response.data.message);
        setDisableContinue(true);
        return false;
      } else {
        setIdExists(false);
        setAlertMessage("");
        return true;
      }
    } catch (error) {
      console.error("Error checking ID number:", error);
      setAlertMessage("An error occurred while checking the ID number.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (event) => {
    const { name, value } = event.target;

    // Update personal details state
    setPersonalDetails({
      ...personalDetails,
      [name]: value,
    });

    if (name === "idNumber") {
      await checkIdNumber();
    }

    // Run validation on every change
    validate();
  };

  return (
    <Grid container spacing={2}>
      {alertMessage && (
        <Grid item xs={12}>
          <Alert severity="error">{alertMessage}</Alert>
        </Grid>
      )}
      
      <Grid item xs={12}>
        <TextField
          label="ID Number"
          fullWidth
          variant="outlined"
          name="idNumber"
          value={personalDetails.idNumber}
          onChange={handleChange}
          onBlur={checkIdNumber}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="First Name"
          fullWidth
          variant="outlined"
          name="firstName"
          value={personalDetails.firstName}
          onChange={handleChange}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Last Name"
          fullWidth
          variant="outlined"
          name="lastName"
          value={personalDetails.lastName}
          onChange={handleChange}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Mobile Number"
          fullWidth
          variant="outlined"
          name="mobile"
          value={personalDetails.mobile}
          onChange={handleChange}
          required
          type="tel"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Email Address"
          fullWidth
          variant="outlined"
          name="email"
          value={personalDetails.email}
          onChange={handleChange}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Password"
          type="password"
          fullWidth
          variant="outlined"
          name="password"
          value={personalDetails.password}
          onChange={handleChange}
          onBlur={handleChange}
          onInput={handleChange}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          variant="outlined"
          name="confirmPassword"
          value={personalDetails.confirmPassword}
          onChange={handleChange}
          onBlur={handleChange}
          onInput={handleChange}
          required
        />
      </Grid>
    </Grid>
  );
});

export default OnBoarding_PersonalDetails;
